import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  useMediaQuery,
  InputAdornment,
  TextField,
  Autocomplete,
  Divider,
  Collapse,
  Grid,
  Button,
} from "@mui/material";
import { SuggestionItem } from "../../new-components/common/SearchBar";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import IconButton from "@mui/material/IconButton";
import { palette } from "../../components/common/Palette";
import SearchIcon from "@mui/icons-material/Search";
import { LocationModalContent } from "./LocationModal";
import { useLocation } from "../../context/NewLocationContext";
import { useTrackEvent } from "../../hooks/useTrackEvent";
import { EventType } from "../../api-data/reporting/enums";
import { useTranslation } from "react-i18next";

const TYPEWRITER_FALLBACK = ["home service"];

// Timing constants (ms)
const HOLD_MS = 1800;   // pause after fully typed
const DELETE_MS = 60;   // delay between each deleted character
const TYPE_MS = 80;     // delay between each typed character
const PAUSE_MS = 400;   // pause before typing the next word

type TypewriterPhase = "typing" | "holding" | "deleting" | "pausing";

interface HomeSearchDialogProps {
  city: string;
  locality?: string;
  searchValue: string;
  suggestions: SuggestionItem[];
  loadingSuggestions?: boolean;
  allowDropdown?: boolean;
  disableSearch?: boolean;
  onSearchChange: (value: string) => void;
  onSearchSelect: (value: string) => void;
  onChangeLocationClick?: () => void;
  categories?: { id: number; categoryName: string }[];
  keywords?: { id: number; keyword: string }[];
  typewriterKeywords?: string[];
  typewriterKeywordsLoading?: boolean;
}

const HomeSearchDialog: React.FC<HomeSearchDialogProps> = ({
  city,
  locality,
  searchValue,
  suggestions,
  loadingSuggestions = false,
  allowDropdown = true,
  disableSearch = false,
  onSearchChange,
  onSearchSelect,
  onChangeLocationClick,
  categories = [],
  keywords = [],
  typewriterKeywords,
  typewriterKeywordsLoading = false,
}) => {
  // ── Typewriter animation ────────────────────────────────────────────────────
  // Only animates the placeholder text when the user hasn't typed anything.
  const words: string[] =
    !typewriterKeywordsLoading && typewriterKeywords && typewriterKeywords.length > 0
      ? typewriterKeywords
      : typewriterKeywordsLoading
      ? []                      // still loading — keep placeholder empty/static
      : TYPEWRITER_FALLBACK;

  const [animatedPlaceholder, setAnimatedPlaceholder] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const phaseRef = useRef<TypewriterPhase>("typing");
  const wordIndexRef = useRef(0);
  const charIndexRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Don't run the animation if the input is focused, user has typed, or words aren't ready
    if (searchFocused || searchValue.trim().length > 0 || words.length === 0) {
      if (timerRef.current) clearTimeout(timerRef.current);
      setAnimatedPlaceholder("");
      return;
    }

    const tick = () => {
      const word = words[wordIndexRef.current % words.length];

      if (phaseRef.current === "typing") {
        charIndexRef.current += 1;
        const partial = word.slice(0, charIndexRef.current);
        setAnimatedPlaceholder(partial);

        if (charIndexRef.current >= word.length) {
          phaseRef.current = "holding";
          timerRef.current = setTimeout(tick, HOLD_MS);
        } else {
          timerRef.current = setTimeout(tick, TYPE_MS);
        }
      } else if (phaseRef.current === "holding") {
        phaseRef.current = "deleting";
        timerRef.current = setTimeout(tick, DELETE_MS);
      } else if (phaseRef.current === "deleting") {
        charIndexRef.current -= 1;
        const partial = word.slice(0, charIndexRef.current);
        setAnimatedPlaceholder(partial);

        if (charIndexRef.current <= 0) {
          wordIndexRef.current = (wordIndexRef.current + 1) % words.length;
          charIndexRef.current = 0;
          phaseRef.current = "pausing";
          timerRef.current = setTimeout(tick, PAUSE_MS);
        } else {
          timerRef.current = setTimeout(tick, DELETE_MS);
        }
      } else {
        // pausing
        phaseRef.current = "typing";
        timerRef.current = setTimeout(tick, TYPE_MS);
      }
    };

    timerRef.current = setTimeout(tick, TYPE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  // words is derived from props; stringify to avoid reference churn
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [words.join("|"), searchValue, searchFocused]);
  // ────────────────────────────────────────────────────────────────────────────

  const isSmall = useMediaQuery((theme: any) => theme.breakpoints.down("md"));
  const [open, setOpen] = React.useState(false);
  const [locationExpanded, setLocationExpanded] = React.useState(false);
  const [resetKey, setResetKey] = React.useState(0);
  const { cities, setLocation, displayCity, displayLocality } = useLocation();
  const { trackEvent } = useTrackEvent();
  const { t } = useTranslation();

  const shouldOpen = allowDropdown && searchValue.trim().length >= 3;

  // Show localized name in the input when searchValue matches a category's English name
  const displaySearchValue = React.useMemo(() => {
    const match = categories.find((c) => c.categoryName?.toLowerCase() === searchValue?.toLowerCase());
    return (match as any)?.localized?.categoryName ?? searchValue;
  }, [categories, searchValue]);

  React.useEffect(() => {
    setOpen(shouldOpen);
  }, [shouldOpen]);

  const options = React.useMemo((): SuggestionItem[] => {
    if (!allowDropdown) return [];
    return suggestions;
  }, [suggestions, allowDropdown]);

  React.useEffect(() => {
    if (open) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [open]);

  // Use translated names from context for display — English values are kept for search/URL
  const locationDisplay = displayLocality ? `${displayCity} · ${displayLocality}` : displayCity;

  const handleArrowSearch = () => {
    if (disableSearch) return;

    const value = searchValue.trim();
    if (!value || value.length < 3) {
      setSearchError(true);
      return;
    }

    setSearchError(false);
    onSearchSelect(value);
    setOpen(false);
  };

  return (
    <Box
      sx={{
        // Solid white card on both breakpoints, matching DesktopHome.jsx.
        backgroundColor: "#fff",
        borderRadius: "18px",
        p: { xs: 1.5, sm: 3 },
        width: "100%",
        boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        border: "none",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        sx={{
          flexWrap: "nowrap",
          mb: 1.2,
          px: 0.5,
        }}
      >
        <Grid size="grow">
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <LocationOnIcon
              sx={{ color: palette.secondary.main, fontSize: 24 }}
            />
            <Typography
              variant="body2"
              sx={{
                color: palette.primary.main,
                fontWeight: 600,
                fontSize: { xs: 15, sm: 15 },
              }}
            >
              {locationDisplay || "Select Location"}
            </Typography>
          </Box>
        </Grid>
        <Grid size="auto">
          <Typography
            onClick={() => {
              if (isSmall) {
                setLocationExpanded((prev) => !prev);
              } else {
                onChangeLocationClick?.();
              }
            }}
            sx={{
              color: palette.secondary.main,
              fontSize: { xs: 12, sm: 14 },
              cursor: "pointer",
              fontWeight: 500,
              display: "flex",
              alignItems: "center",
              gap: 0.2,
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            <KeyboardArrowDownIcon
              sx={{
                fontSize: 24,
                transition: "transform 0.3s",
                transform:
                  locationExpanded && isSmall
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
              }}
            />
          </Typography>
        </Grid>
      </Grid>

      {isSmall && (
        <Collapse in={locationExpanded} timeout={300}>
          <Box sx={{ pt: 2, pb: 3 }}>
            <LocationModalContent
              cities={cities}
              initialCity={city}
              initialLocality={locality}
              resetKey={resetKey}
              onApply={(newCity, newLocality, newCityId, newLocalityId) => {
                setLocation(newCity, newLocality, newCityId, newLocalityId);

                // Track CITY_SELECTED event when Apply button is clicked
                trackEvent(EventType.CITY_SELECTED, {
                  cityId: newCityId,
                  localityId: newLocalityId,
                  source: "home_page_mobile",
                });

                setLocationExpanded(false);
                setResetKey((k) => k + 1);
              }}
            />
          </Box>
        </Collapse>
      )}

      <Collapse in={!locationExpanded} timeout={300}>
        <Grid
          container
          sx={{ mt: isSmall && !locationExpanded ? 1.2 : 0, mb: 1.2 }}
        >
          <Grid size={12}>
            <Autocomplete<SuggestionItem, false, false, true>
              freeSolo
              fullWidth
              open={open}
              onClose={() => setOpen(false)}
              options={options}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option.label
              }
              loading={loadingSuggestions}
              loadingText="Loading…"
              noOptionsText="No keywords found"
              filterOptions={(x) => x}
              inputValue={displaySearchValue}
              onInputChange={(_, v, reason) => {
                if (reason === "input" || reason === "clear") {
                  if (searchError) setSearchError(false);
                  onSearchChange(v);
                }
              }}
              onChange={(_, v) => {
                if (v && typeof v !== "string") {
                  onSearchSelect(v.label);
                  setOpen(false);
                }
              }}
              slotProps={{
                paper: {
                  sx: {
                    mt: 1,
                    backgroundColor: "white",
                    boxShadow: "0 12px 24px rgba(0,0,0,0.15)",
                    borderRadius: "16px",
                    border: "1px solid rgba(0,0,0,0.1)",
                    "& .MuiAutocomplete-listbox": {
                      p: 1,
                      backgroundColor: "white",
                      overscrollBehavior: "contain",
                    },
                    "& .MuiAutocomplete-option": {
                      borderRadius: "8px",
                      py: 1.5,
                      px: 2,
                      fontSize: 15,
                      '&[aria-selected="true"]': {
                        backgroundColor: "rgba(1, 175, 174, 0.08)",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "rgba(1, 175, 174, 0.04)",
                      },
                      "&:hover": {
                        backgroundColor: "rgba(1, 175, 174, 0.04)",
                      },
                    },
                  },
                },
              }}
              renderOption={(props, option, { index }) => {
                const item = typeof option === "string"
                  ? { label: option, categoryName: null }
                  : option;
                return (
                  <Box key={item.label} sx={{ width: "100%" }}>
                    <Box component="li" {...props}>
                      <Box>
                        <Typography sx={{ fontSize: 14, lineHeight: 1.3 }}>
                          {item.label}
                        </Typography>
                        {item.categoryName && (
                          <Typography
                            sx={{ fontSize: 12, color: "primary.main", lineHeight: 1.3 }}
                          >
                            in {item.categoryName}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    {index < suggestions.length - 1 && (
                      <Divider
                        sx={{ my: 0.5, borderColor: "rgba(0,0,0,0.06)" }}
                      />
                    )}
                  </Box>
                );
              }}
              renderInput={(params) => (
                <Box sx={{ position: "relative" }}>
                  {/* Typewriter overlay — visible only when input is empty and not focused */}
                  {searchValue.trim().length === 0 && !searchFocused && (
                    <Box
                      aria-hidden="true"
                      sx={{
                        position: "absolute",
                        left: 52,          // clears the search icon adornment
                        top: "50%",
                        transform: "translateY(-50%)",
                        pointerEvents: "none",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.25ch",
                        fontSize: 14,
                        lineHeight: "normal",
                        color: "rgba(0,0,0,0.38)",  // matches MUI placeholder colour
                        zIndex: 1,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span>{t("home.searchFor")}</span>
                      {animatedPlaceholder
                        ? <span>&ldquo;{animatedPlaceholder}&rdquo;</span>
                        : null}
                    </Box>
                  )}
                  <TextField
                    {...params}
                    placeholder={
                      searchValue.trim().length === 0 && !searchFocused
                        ? ""
                        : t("home.searchPlaceholder")
                    }
                    variant="outlined"
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <Box
                              sx={{
                                color: palette.text.disabled,
                                ml: 0.5,
                                display: "flex",
                              }}
                            >
                              <SearchIcon fontSize="small" />
                            </Box>
                          </InputAdornment>
                        ),
                      endAdornment: (
                        <InputAdornment
                          position="end"
                          sx={{
                            p: 0,
                            m: 0,
                            height: "100%",
                            maxHeight: "none",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          {params.InputProps.endAdornment}
                          {!isSmall && (
                            <Button
                              onClick={handleArrowSearch}
                              variant="contained"
                              color="primary"
                              sx={{
                                borderTopLeftRadius: 0,
                                borderBottomLeftRadius: 0,
                                borderTopRightRadius: "45px",
                                borderBottomRightRadius: "45px",
                                textTransform: "none",
                                px: 4,
                                height: "48px",
                                m: 0,
                                minHeight: "48px",
                                fontSize: "14px",
                                fontWeight: 600,
                                boxShadow: "none",
                                "&:hover": {
                                  boxShadow: "none",
                                },
                              }}
                            >
                              {t("home.find")}
                            </Button>
                          )}
                          {isSmall && (
                            <IconButton
                              onClick={handleArrowSearch}
                              disabled={disableSearch}
                              aria-label={t("home.find")}
                              sx={{
                                mr: 0.5,
                                width: 32,
                                height: 32,
                                bgcolor: "secondary.main",
                                color: "primary.dark",
                                "&:hover": { bgcolor: "secondary.main" },
                                "&.Mui-disabled": { bgcolor: "action.disabledBackground" },
                              }}
                            >
                              <ArrowForwardIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                          )}
                        </InputAdornment>
                        ),
                      },
                      htmlInput: {
                        ...params.inputProps,
                        "data-searchbar": true,
                        onFocus: () => setSearchFocused(true),
                        onBlur: () => setSearchFocused(false),
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "45px",
                        backgroundColor: "white",
                        height: 48,
                        fontSize: 14,
                        paddingLeft: 2,
                        paddingRight: "0 !important",
                        "& .MuiAutocomplete-endAdornment": {
                          position: "static",
                          display: "flex",
                          alignItems: "center",
                          height: "100%",
                          top: "auto",
                          transform: "none",
                          pr: 1,
                          "& .MuiIconButton-root": {
                            padding: "8px",
                            margin: 0,
                          },
                          "& .MuiSvgIcon-root": {
                            fontSize: 24,
                          },
                        },
                        "& fieldset": {
                          borderColor: "rgba(0,0,0,0.12)",
                        },
                        "&:hover fieldset": {
                          borderColor: palette.primary.main,
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: palette.primary.main,
                          borderWidth: "2px",
                        },
                      },
                    }}
                  />
              </Box>
            )}
            />
            {searchError && (
              <Typography
                sx={{
                  color: "error.main",
                  fontSize: "12px",
                  mt: 0.75,
                  ml: 2,
                }}
              >
                {t("search.keywordRequired")}
              </Typography>
            )}
          </Grid>
        </Grid>

        <Grid
          container
          justifyContent="center"
          spacing={{ xs: 2.5, sm: 4 }}
          sx={{ flexWrap: "wrap", mt: 0.5 }}
        >
          <Grid size="auto">
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <CheckIcon sx={{ color: palette.primary.main, fontSize: 16 }} />
              <Typography
                variant="caption"
                sx={{ color: palette.text.secondary, fontWeight: 500 }}
              >
                {t("home.verifiedExperts")}
              </Typography>
            </Box>
          </Grid>
          <Grid size="auto">
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <CheckIcon sx={{ color: palette.primary.main, fontSize: 16 }} />
              <Typography
                variant="caption"
                sx={{ color: palette.text.secondary, fontWeight: 500 }}
              >
                {t("home.noHiddenCharges")}
              </Typography>
            </Box>
          </Grid>
          {!isSmall && (
            <Grid size="auto">
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <CheckIcon sx={{ color: palette.primary.main, fontSize: 16 }} />
                <Typography
                  variant="caption"
                  sx={{ color: palette.text.secondary, fontWeight: 500 }}
                >
                  {t("home.payAfterService")}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </Collapse>
    </Box>
  );
};

export default HomeSearchDialog;
